import React from 'react';
import { Link } from 'react-router-dom';
import { 
  FaLeaf, 
  FaSearch, 
  FaHandHoldingHeart, 
  FaClipboardCheck, 
  FaAward, 
  FaRegStar,
  FaTruck,
  FaRecycle
} from 'react-icons/fa';
import { GiTomato, GiCarrot, GiBroccoli } from 'react-icons/gi';

const QualityPage = () => {
  const qualityPillars = [
    {
      icon: <FaLeaf className="text-4xl text-green-600" />,
      title: 'Farm to Fork Traceability',
      desc: 'We track every vegetable from seed to delivery, ensuring full transparency.',
    },
    {
      icon: <FaSearch className="text-4xl text-green-600" />,
      title: 'Rigorous Inspections',
      desc: 'Multi-stage quality checks at farms, warehouses, and before dispatch.',
    },
    {
      icon: <FaHandHoldingHeart className="text-4xl text-green-600" />,
      title: 'Ethical & Sustainable',
      desc: 'Partnering with farms that follow fair labor and eco-friendly practices.',
    },
    {
      icon: <FaClipboardCheck className="text-4xl text-green-600" />,
      title: 'Certified Standards',
      desc: 'Compliance with GlobalG.A.P., Organic, and BRC certifications.',
    },
  ];

  const certifications = [
    { name: 'GlobalG.A.P.', icon: <FaAward className="text-5xl text-green-600" /> },
    { name: 'Organic Certified', icon: <FaLeaf className="text-5xl text-green-600" /> },
    { name: 'BRC Food Safety', icon: <FaClipboardCheck className="text-5xl text-green-600" /> },
    { name: 'ISO 22000', icon: <FaRegStar className="text-5xl text-yellow-500" /> },
  ];

  const testimonials = [
    {
      quote: "Freshigo quality is unmatched. Every delivery is consistently fresh and well-packed.",
      name: 'Rajesh Mehta',
      role: 'Restaurant Owner',
      image: '/testimonial1.jpg',
    },
    {
      quote: "Their quality control gives us confidence. We've been partners for 5 years.",
      name: 'Sunita Reddy',
      role: 'Retail Chain Buyer',
      image: '/testimonial2.jpg',
    },
    {
      quote: "The traceability and freshness are excellent. Highly recommended.",
      name: 'Arjun Singh',
      role: 'Wholesaler',
      image: '/testimonial3.jpg',
    },
  ];

  const processSteps = [
    { step: '1', title: 'Farm Selection', desc: 'We partner with certified, inspected farms.', icon: <GiCarrot /> },
    { step: '2', title: 'Pre-Harvest Checks', desc: 'Soil, water, and pesticide residue testing.', icon: <FaLeaf /> },
    { step: '3', title: 'Harvesting', desc: 'Hand-picked at peak ripeness.', icon: <GiTomato /> },
    { step: '4', title: 'Grading & Sorting', desc: 'Size, color, and quality grading.', icon: <FaClipboardCheck /> },
    { step: '5', title: 'Cold Storage', desc: 'Temperature-controlled storage.', icon: <FaTruck /> },
    { step: '6', title: 'Final Inspection', desc: 'Random sampling before dispatch.', icon: <FaSearch /> },
  ];

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-green-700 to-green-800 text-white py-24">
        <div className="absolute inset-0 overflow-hidden">
          <img src="/quality-hero.jpg" alt="Quality vegetables" className="w-full h-full object-cover opacity-20" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6 animate-fadeInDown">Our Quality Commitment</h1>
          <p className="text-xl md:text-2xl max-w-3xl mx-auto text-green-100">
            We never compromise on quality – because you deserve the best.
          </p>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-green-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-green-800 mb-6">Quality is Our Promise</h2>
          <p className="text-lg text-gray-700 leading-relaxed">
            At Freshigo Vegetables, quality isn't just a department – it's the foundation of everything we do. From the moment a seed is planted to the time it reaches your kitchen, we maintain the highest standards of freshness, safety, and integrity. Our multi-layered quality assurance process ensures that every vegetable we deliver meets your expectations and ours.
          </p>
        </div>
      </section>

      {/* Quality Pillars */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-green-800 mb-4">Our Quality Pillars</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              The principles that ensure excellence at every step.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {qualityPillars.map((pillar, index) => (
              <div
                key={index}
                className="bg-white p-8 rounded-xl shadow-lg hover:shadow-2xl transform hover:-translate-y-2 transition duration-300 text-center"
              >
                <div className="flex justify-center mb-4">{pillar.icon}</div>
                <h3 className="text-xl font-bold text-green-700 mb-2">{pillar.title}</h3>
                <p className="text-gray-600">{pillar.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="bg-green-50 py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-green-800 mb-4">Our Certifications</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Recognized by leading global standards.
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {certifications.map((cert, index) => (
              <div
                key={index}
                className="bg-white p-6 rounded-xl shadow-md text-center hover:shadow-xl transition duration-300"
              >
                <div className="flex justify-center mb-3">{cert.icon}</div>
                <p className="font-semibold text-gray-700">{cert.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quality Control Process */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-green-800 mb-4">Our Quality Control Process</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From farm to fork – every step is monitored.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {processSteps.map((step, index) => (
              <div
                key={index}
                className="relative bg-white p-6 rounded-xl shadow-md hover:shadow-xl transition duration-300 flex items-start space-x-4"
              >
                <div className="bg-green-100 w-12 h-12 rounded-full flex items-center justify-center text-green-700 font-bold text-xl">
                  {step.step}
                </div>
                <div>
                  <h3 className="text-lg font-bold text-green-700 mb-1">{step.title}</h3>
                  <p className="text-gray-600">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 px-4 bg-gradient-to-r from-green-700 to-green-800 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-4">Experience the Quality</h2>
          <p className="text-xl text-green-200 mb-8">
            Browse our selection of premium, quality-assured vegetables.
          </p>
          <Link
            to="/products"
            className="inline-flex items-center px-8 py-4 bg-white text-green-700 font-semibold rounded-lg shadow-lg hover:bg-green-100 transform hover:scale-105 transition duration-300"
          >
            Shop Now
          </Link>
        </div>
      </section>
    </div>
  );
};

export default QualityPage;